import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../utils/api';
import { readAdminApiErrorMessage } from '../utils/adminUi';
import { showToast } from '../utils/toast';
import SearchableDropdown from './shared/SearchableDropdown';
import { trackEvent } from '../services/applicationInsights';
import {
  FaFileAlt,
  FaFilter,
  FaTrash,
  FaCheck,
  FaTimes,
  FaSearch,
  FaSyncAlt,
  FaCircle,
  FaExternalLinkAlt,
  FaChevronLeft,
  FaChevronRight,
  FaFolderOpen,
  FaQuestionCircle,
  FaCubes,
  FaCheckCircle,
  FaHourglassHalf,
  FaBan
} from "react-icons/fa";

export default function Contents({ token, roles = [], orgId }) {
  const roleList = Array.isArray(roles) ? roles : [];
  const isSystemAdmin = roleList.some(r => String(r).toLowerCase() === 'systemadministrator');
  const isOrgAdmin = roleList.some(r => String(r).toLowerCase() === 'organizationadministrator');
  const tableRef = useRef(null);
  const fetchSitesAbortRef = useRef(null);
  const fetchContentsAbortRef = useRef(null);

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(() => {
    return isSystemAdmin ? '' : (orgId || '');
  });
  const [sitesForLoad, setSitesForLoad] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [contents, setContents] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const [loading, setLoading] = useState(false);
  const [fetchingOrgs, setFetchingOrgs] = useState(false);
  const [fetchingSites, setFetchingSites] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const autoSyncedContentsKeyRef = useRef('');

  useEffect(() => {
    return () => {
      fetchSitesAbortRef.current?.abort();
      fetchContentsAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!isSystemAdmin && orgId && selectedOrg !== orgId) {
      setSelectedOrg(orgId);
    }
  }, [isSystemAdmin, orgId, selectedOrg]);

  const siteIdsFromToken = (() => {
    try {
      if (!token) return [];
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      return (payload?.site_ids || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    } catch (e) { return []; }
  })();

  const isOwnedSite = (siteId) => siteIdsFromToken.includes(String(siteId).toLowerCase());
  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const parseContentFetchError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      400: 'The selected site context is invalid. Please reselect and try again.',
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to access content for this site.',
      404: 'The selected content source was not found.',
      500: 'An error occurred while retrieving content resources.',
      default: 'Failed to fetch content resources.'
    });
  }, []);

  const parseContentActionError = useCallback((res, type) => {
    const defaultByType = {
      DELETE: 'Failed to delete content.',
      APPROVE: 'Failed to approve content.',
      REJECT: 'Failed to reject content.',
      DISABLE: 'Failed to disable content.'
    };

    return readAdminApiErrorMessage(res, {
      400: 'The request is invalid. Please verify the selected content action.',
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to perform this content action.',
      404: 'The selected content item no longer exists.',
      409: 'This content action conflicts with the current workflow state.',
      500: 'An unexpected server error occurred while processing the content action.',
      default: defaultByType[type] || 'Content action failed.'
    });
  }, []);

  const loadContents = useCallback(async () => {
    if (!selectedSite) return;
    fetchContentsAbortRef.current?.abort();
    const controller = new AbortController();
    fetchContentsAbortRef.current = controller;

    setLoading(true);
    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    const buildUrl = (includeDisabled) => {
      const base = `${API_BASE_URL}/contents/site-collections/${selectedSite}?organizationId=${orgIdToUse}`;
      return includeDisabled ? `${base}&includeDisabled=true` : base;
    };

    try {
      let res = await fetch(buildUrl(true), {
        headers: getHeaders(),
        signal: controller.signal
      });

      if (!res.ok && res.status === 400) {
        res = await fetch(buildUrl(false), {
          headers: getHeaders(),
          signal: controller.signal
        });
      }

      if (!res.ok) {
        const message = await parseContentFetchError(res);
        throw new Error(message);
      }

      const data = await res.json().catch(() => []);

      setContents(Array.isArray(data) ? data : []);
      setCurrentPage(1);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      showToast.error(err.message || 'Failed to fetch content resources.');
      setContents([]);
    } finally {
      if (fetchContentsAbortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [selectedSite, isSystemAdmin, selectedOrg, orgId, getHeaders, parseContentFetchError]);

  useEffect(() => {
    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    const hasRequiredSelections = Boolean(orgIdToUse && selectedSite);

    if (!hasRequiredSelections) {
      autoSyncedContentsKeyRef.current = '';
      fetchContentsAbortRef.current?.abort();
      setContents([]);
      return;
    }

    const syncKey = `${orgIdToUse}:${selectedSite}`;
    if (autoSyncedContentsKeyRef.current === syncKey) {
      return;
    }

    autoSyncedContentsKeyRef.current = syncKey;
    loadContents();
  }, [selectedSite, selectedOrg, orgId, isSystemAdmin, loadContents]);

  const handleKpiClick = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const executeAction = async () => {
    if (!confirmation) return;
    const { id, type } = confirmation;
    setSubmitting(true);
    setConfirmation(null);

    try {
      const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;

      if (type === 'DELETE') {
        const res = await fetch(`${API_BASE_URL}/contents/${id}`, {
          method: 'DELETE',
          headers: getHeaders()
        });

        if (!res.ok) {
          const message = await parseContentActionError(res, type);
          throw new Error(message);
        }

        showToast.success('Content deleted successfully');
      } else if (type === 'APPROVE' || type === 'REJECT') {
        const statusMap = {
          'APPROVE': 'Approved',
          'REJECT': 'Rejected'
        };

        const statusValue = statusMap[type];

        const requestFormats = [
          {
            request: {
              action: statusValue,
              artifactId: id
            }
          },
          {
            action: statusValue,
            artifactId: id
          },
          {
            action: statusValue,
            artifactId: id,
            artifactType: "Content"
          },
          {
            request: {
              action: statusValue,
              artifactId: id,
              artifactType: "Content"
            }
          }
        ];

        let lastError = null;
        let successResponse = false;

        for (let i = 0; i < requestFormats.length; i++) {
          const requestBody = requestFormats[i];

          try {
            const res = await fetch(`${API_BASE_URL}/workflows/approve-or-reject?organizationId=${orgIdToUse}`, {
              method: 'PUT',
              headers: getHeaders(),
              body: JSON.stringify(requestBody)
            });

            if (res.ok) {
              successResponse = true;
              const actionText = type === 'APPROVE' ? 'approved' : 'rejected';
              showToast.success(`Content ${actionText} successfully`);
              break;
            } else {
              lastError = await parseContentActionError(res, type);
            }
          } catch (e) {
            lastError = e.message;
          }
        }

        if (!successResponse) {
          throw new Error(lastError || 'Failed to update content workflow status.');
        }
      } else if (type === 'DISABLE') {
        const requestBody = {
          artifactType: "Content",
          action: "Disabled",
          artifactId: id
        };

        try {
          const res = await fetch(`${API_BASE_URL}/workflows/disable?organizationId=${orgIdToUse}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(requestBody)
          });

          if (!res.ok) {
            const message = await parseContentActionError(res, type);
            throw new Error(message || 'Failed to disable content');
          }

          showToast.success('Content disabled successfully');

          setContents(prevContents =>
            prevContents.map(c =>
              c.id === id ? { ...c, status: 'Disabled' } : c
            )
          );

          setStatusFilter('All');
          setCurrentPage(1);
        } catch (e) {
          throw new Error(`Failed to disable content: ${e.message}`);
        }
      }

      try {
        await loadContents();
      } catch (reloadErr) {
        // ignore reload error
      }
    } catch (err) {
      showToast.error(err.message || 'Content operation failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const isDirectLink = (value) => {
    if (!value) return false;
    const s = String(value).trim();

    // Allow plain http(s) links
    if (/^https?:\/\//i.test(s)) return true;

    // Optional: also allow "www." style links
    if (/^www\./i.test(s)) return true;

    return false;
  };

  const openContentInNewTabWithFeedback = useCallback((url) => {
    const opened = window.open(url, '_blank', 'noopener,noreferrer');

    if (opened) {
      showToast.success('Content view launched in a new tab.');
      return;
    }

    showToast.info('Content view launched. If no new tab opened, please allow pop-ups for this site and retry.');
  }, []);

  const handleView = async (content) => {
    try {
      const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;

      const contentType = String(content?.contentType || '').toLowerCase();
      const contentData = content?.contentData;

      if (contentType === 'link' && isDirectLink(contentData)) {
        const directUrl = /^www\./i.test(String(contentData).trim())
          ? `https://${String(contentData).trim()}`
          : String(contentData).trim();

        openContentInNewTabWithFeedback(directUrl);
        return;
      }

      // default: presigned download flow
      const res = await fetch(
        `${API_BASE_URL}/contents/${encodeURIComponent(contentData)}/download-url?organizationId=${orgIdToUse}`,
        { headers: getHeaders() }
      );

      if (!res.ok) {
        const message = await readAdminApiErrorMessage(res, {
          401: 'Your session is invalid or expired. Please sign in again.',
          403: 'You do not have permission to open this content.',
          404: 'The requested content file was not found.',
          500: 'Unable to open content due to a server error.',
          default: 'Unable to open content at this time.'
        });
        throw new Error(message);
      }

      const data = await res.json();
      const url = data.presignedUrl || data.presignedDownloadUrl;

      if (!url) throw new Error('No presigned URL returned');
      openContentInNewTabWithFeedback(url);
    } catch (err) {
      showToast.error(err?.message || 'Unable to open content.');
    }
  };

  // Load Organizations
  useEffect(() => {
    if (isSystemAdmin) {
      setFetchingOrgs(true);
      fetch(`${API_BASE_URL}/organizations`, { headers: getHeaders() })
        .then(async (res) => {
          if (!res.ok) {
            const message = await readAdminApiErrorMessage(res, {
              401: 'Your session is invalid or expired. Please sign in again.',
              403: 'You do not have permission to view organizations.',
              500: 'An error occurred while loading organizations.',
              default: 'Failed to load organizations.'
            });
            throw new Error(message);
          }

          return res.json().catch(() => []);
        })
        .then(data => {
          const list = Array.isArray(data) ? data : [];
          setOrganizations(list);
          setSelectedOrg((prev) => {
            if (prev && !list.some((o) => o.id === prev)) {
              return '';
            }
            return prev;
          });
        })
        .catch((err) => showToast.error(err?.message || 'Error loading organizations.'))
        .finally(() => setFetchingOrgs(false));
    }
  }, [isSystemAdmin, getHeaders]);

  // Load Sites and reset Site when Org changes
  useEffect(() => {
    const orgToUse = isSystemAdmin ? selectedOrg : orgId;
    if (orgToUse) {
      setFetchingSites(true);
      fetchSitesAbortRef.current?.abort();
      const controller = new AbortController();
      fetchSitesAbortRef.current = controller;

      fetch(`${API_BASE_URL}/site-collections?organizationId=${orgToUse}`, {
        headers: getHeaders(),
        signal: controller.signal
      })
        .then(async (res) => {
          if (!res.ok) {
            const message = await readAdminApiErrorMessage(res, {
              401: 'Your session is invalid or expired. Please sign in again.',
              403: 'You do not have permission to view site collections.',
              404: 'Site collections for the selected organization were not found.',
              500: 'An error occurred while loading site collections.',
              default: 'Failed to load site collections.'
            });
            throw new Error(message);
          }

          return res.json().catch(() => []);
        })
        .then(data => {
          const list = Array.isArray(data) ? data : [];
          setSitesForLoad(list);
          setSelectedSite((prev) => {
            if (prev && !list.some((s) => s.id === prev)) {
              return '';
            }
            return prev;
          });
        })
        .catch((err) => {
          if (err?.name === 'AbortError') return;
          showToast.error(err?.message || 'Error loading site collections.');
          setSitesForLoad([]);
          setSelectedSite('');
        })
        .finally(() => {
          if (fetchSitesAbortRef.current === controller) {
            setFetchingSites(false);
          }
        });
    } else {
      fetchSitesAbortRef.current?.abort();
      setSitesForLoad([]);
      setSelectedSite('');
      setFetchingSites(false);
    }
    // Reset site and contents when organization changes
    setSelectedSite('');
    setContents([]);
    setCurrentPage(1);
    setStatusFilter('All');
    setSearchTerm('');
  }, [selectedOrg, orgId, isSystemAdmin, getHeaders]);

  // Reset contents table when site changes
  useEffect(() => {
    setContents([]);
    setCurrentPage(1);
    setStatusFilter('All');
    setSearchTerm('');
  }, [selectedSite]);

  const filteredContents = useMemo(() => {
    return contents.filter(c => {
      const matchesSearch = c.contentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.contentDescription || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'All' || c.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [contents, searchTerm, statusFilter]);

  const totalPages = Math.ceil(filteredContents.length / itemsPerPage);
  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredContents.slice(start, start + itemsPerPage);
  }, [filteredContents, currentPage]);

  const visiblePages = useMemo(() => {
    const maxVisible = 5;
    if (totalPages <= maxVisible) return Array.from({ length: totalPages }, (_, i) => i + 1);
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = start + maxVisible - 1;
    if (end > totalPages) { end = totalPages; start = end - maxVisible + 1; }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  const getBadgeStyle = (status) => {
    const base = {
      padding: '6px 12px',
      borderRadius: '8px',
      fontSize: '11px',
      fontWeight: 700,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      transition: 'all 0.2s ease'
    };

    if (status === 'Approved') return { ...base, background: '#dcfce7', color: '#166534' };
    if (status === 'Rejected') return { ...base, background: '#fee2e2', color: '#991b1b' };
    if (status === 'Submitted') return { ...base, background: '#e9d5ff', color: '#581c87' };
    if (status === 'Pending') return { ...base, background: '#fef3c7', color: '#92400e' };
    if (status === 'Disabled') {
      return {
        ...base,
        background: '#f3f4f6',
        color: '#6b7280',
        border: '1px solid #d1d5db',
        fontWeight: 600,
        opacity: 0.85
      };
    }
    return base;
  };

  return (
    <div style={wrapperStyle}>
      <div style={containerStyle}>

        <header style={headerStyle}>
          <div style={headerContentStyle}>
            <div style={headerBadgeStyle}><FaFileAlt size={22} /></div>
            <div><h1 style={titleStyle}>Content Management</h1></div>
          </div>
        </header>

        {/* SELECTION CRITERIA CARD */}
        <div style={filtersCardStyle}>
          <div style={cardHeaderStyle}><FaFilter color="#6366f1" size={14} /><span style={cardTitleStyle}>Selection Criteria</span></div>
          <div style={filtersGridStyle}>
            {isSystemAdmin && (
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Organization</label>
                <SearchableDropdown
                  options={organizations}
                  selectedValue={selectedOrg}
                  onSelect={(value) => {
                    setSelectedOrg(value);
                    setSelectedSite('');
                    if (value && selectedSite) {
                      trackEvent('Dropdown Selected', {
                        section: 'contents',
                        organizationId: value,
                        siteCollectionId: selectedSite
                      });
                    }
                  }}
                  placeholder="Choose Organization"
                  displayKey="name"
                  valueKey="id"
                  isLoading={fetchingOrgs}
                  disabled={fetchingOrgs}
                  styles={{
                    container: dropdownContainerStyle,
                    trigger: dropdownTriggerStyle,
                    triggerText: dropdownTriggerTextStyle,
                    menu: dropdownMenuStyle,
                    searchContainer: searchBoxContainerStyle,
                    searchIcon: searchIconStyle,
                    searchInput: dropdownSearchInputStyle,
                    list: dropdownListStyle,
                    option: dropdownOptionStyle,
                    optionText: optionTextStyle,
                    optionName: optionNameStyle,
                    optionId: optionIdStyle,
                    emptyOption: emptyOptionStyle
                  }}
                />
              </div>
            )}

            <div style={inputGroupStyle}>
              <label style={labelStyle}>Site Collection</label>
              <SearchableDropdown
                options={sitesForLoad}
                selectedValue={selectedSite}
                onSelect={(value) => {
                  setSelectedSite(value);
                  const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
                  if (value && orgIdToUse) {
                    trackEvent('Dropdown Selected', {
                      section: 'contents',
                      organizationId: orgIdToUse,
                      siteCollectionId: value
                    });
                  }
                }}
                placeholder={!selectedOrg && isSystemAdmin ? 'Select Org First' : 'Choose Site'}
                displayKey="name"
                valueKey="id"
                isLoading={fetchingSites}
                disabled={fetchingSites || (isSystemAdmin && !selectedOrg)}
                emptyMessage={sitesForLoad.length === 0 ? 'No sites available' : 'No results found'}
                styles={{
                  container: dropdownContainerStyle,
                  trigger: dropdownTriggerStyle,
                  triggerText: dropdownTriggerTextStyle,
                  menu: dropdownMenuStyle,
                  searchContainer: searchBoxContainerStyle,
                  searchIcon: searchIconStyle,
                  searchInput: dropdownSearchInputStyle,
                  list: dropdownListStyle,
                  option: dropdownOptionStyle,
                  optionText: optionTextStyle,
                  optionName: optionNameStyle,
                  optionId: optionIdStyle,
                  emptyOption: emptyOptionStyle
                }}
              />
            </div>

            <div style={{ ...inputGroupStyle, justifyContent: 'flex-end' }}>
              <button
                style={{
                  ...primaryButtonStyle,
                  opacity: !selectedSite || loading || submitting ? 0.6 : 1,
                  cursor: !selectedSite || loading || submitting ? 'not-allowed' : 'pointer'
                }}
                onClick={loadContents}
                disabled={!selectedSite || loading || submitting}
              >
                <FaSyncAlt style={{ animation: (loading || submitting) ? 'spin 1s linear infinite' : 'none' }} />
                {(loading || submitting) ? 'Syncing...' : 'Sync Contents'}
              </button>
            </div>
          </div>
        </div>

        {/* KPI ROW */}
        <div style={kpiRowStyle}>
          <div className="kpi-card" style={statusFilter === 'All' ? kpiCardActiveAll : kpiCardWhite} onClick={() => handleKpiClick('All')}>
            <div style={kpiHeader}>
              <div style={{ ...kpiIconWrapper, background: '#f5f3ff', color: '#6366f1' }}><FaCubes /></div>
              <span style={kpiLabelStyle}>Total Items</span>
            </div>
            <div style={kpiValueStyle}>{contents.length}</div>
            <div style={kpiMetaStyle}>All content items</div>
          </div>

          <div className="kpi-card" style={statusFilter === 'Pending' ? kpiCardActivePending : kpiCardWhite} onClick={() => handleKpiClick('Pending')}>
            <div style={kpiHeader}>
              <div style={{ ...kpiIconWrapper, background: '#fffbeb', color: '#d97706' }}><FaHourglassHalf /></div>
              <span style={kpiLabelStyle}>Pending</span>
            </div>
            <div style={{ ...kpiValueStyle, color: '#d97706' }}>{contents.filter(c => c.status === 'Pending').length}</div>
            <div style={kpiMetaStyle}>Draft items</div>
          </div>

          <div className="kpi-card" style={statusFilter === 'Submitted' ? kpiCardActiveSubmitted : kpiCardWhite} onClick={() => handleKpiClick('Submitted')}>
            <div style={kpiHeader}>
              <div style={{ ...kpiIconWrapper, background: '#f3e8ff', color: '#7c3aed' }}><FaHourglassHalf /></div>
              <span style={kpiLabelStyle}>Submitted</span>
            </div>
            <div style={{ ...kpiValueStyle, color: '#7c3aed' }}>{contents.filter(c => c.status === 'Submitted').length}</div>
            <div style={kpiMetaStyle}>Awaiting approval</div>
          </div>

          <div className="kpi-card" style={statusFilter === 'Approved' ? kpiCardActiveApproved : kpiCardWhite} onClick={() => handleKpiClick('Approved')}>
            <div style={kpiHeader}>
              <div style={{ ...kpiIconWrapper, background: '#f0fdf4', color: '#16a34a' }}><FaCheckCircle /></div>
              <span style={kpiLabelStyle}>Approved</span>
            </div>
            <div style={{ ...kpiValueStyle, color: '#10b981' }}>{contents.filter(c => c.status === 'Approved').length}</div>
            <div style={kpiMetaStyle}>Active items</div>
          </div>

          <div className="kpi-card" style={statusFilter === 'Rejected' ? kpiCardActiveRejected : kpiCardWhite} onClick={() => handleKpiClick('Rejected')}>
            <div style={kpiHeader}>
              <div style={{ ...kpiIconWrapper, background: '#fef2f2', color: '#dc2626' }}><FaTimes /></div>
              <span style={kpiLabelStyle}>Rejected</span>
            </div>
            <div style={{ ...kpiValueStyle, color: '#ef4444' }}>{contents.filter(c => c.status === 'Rejected').length}</div>
            <div style={kpiMetaStyle}>Declined items</div>
          </div>

          <div className="kpi-card" style={statusFilter === 'Disabled' ? kpiCardActiveDisabled : kpiCardWhite} onClick={() => handleKpiClick('Disabled')}>
            <div style={kpiHeader}>
              <div style={{ ...kpiIconWrapper, background: '#f3f4f6', color: '#6b7280' }}><FaBan /></div>
              <span style={kpiLabelStyle}>Disabled</span>
            </div>
            <div style={{ ...kpiValueStyle, color: '#6b7280' }}>{contents.filter(c => c.status === 'Disabled').length}</div>
            <div style={kpiMetaStyle}>Inactive items</div>
          </div>
        </div>

        {/* TABLE CARD */}
        <div ref={tableRef} style={tableCardStyle}>
          <div style={toolbarStyle}>
            <div style={searchWrapper}>
              <FaSearch style={searchIcon} />
              <input style={searchInput} placeholder="Search resources..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
            </div>
          </div>

          <table style={tableStyle}>
            <thead>
              <tr style={tableHeaderRowStyle}>
                <th style={thStyle}>Resource Detail</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Status</th>
                <th style={thActionStyle}>Action Control</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" style={emptyStateStyle}><FaSyncAlt className="spin" /> Syncing...</td></tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="4" style={emptyStateStyle}>
                    <FaFolderOpen size={32} opacity={0.3} />
                    <div style={{ marginTop: 10 }}>
                      {isSystemAdmin && !selectedOrg
                        ? 'Select an organization to begin'
                        : !selectedSite
                          ? 'Select a site to begin'
                          : 'No contents found'}
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((content) => (
                  <tr key={content.id} style={trStyle}>
                    <td style={tdStyle}>
                      <div style={contentNameText}>{content.contentName}</div>
                      <div style={contentIdText}>ID: {content.id}</div>
                      <div style={contentDescText}>{content.contentDescription || 'No description'}</div>
                    </td>
                    <td style={tdStyle}><span style={typeBadgeStyle}>{content.contentType}</span></td>
                    <td style={tdStyle}>
                      <span style={getBadgeStyle(content.status)}><FaCircle size={6} /> {content.status}</span>
                    </td>
                    <td style={tdActionStyle}>
                      {confirmation?.id === content.id ? (
                        <div style={inlineConfirmWrapper}>
                          <FaQuestionCircle color="#6366f1" />
                          <span style={confirmText}>Confirm {confirmation.type === 'APPROVE' ? 'Approve' : confirmation.type === 'REJECT' ? 'Reject' : confirmation.type === 'DISABLE' ? 'Disable' : 'Delete'}?</span>
                          <button style={btnYes} onClick={executeAction} disabled={submitting}>Yes</button>
                          <button style={btnNo} onClick={() => setConfirmation(null)} disabled={submitting}>No</button>
                        </div>
                      ) : (
                        <div style={actionButtonGroupStyle}>
                          {String(content.contentType || '').toLowerCase() !== 'knowledgebase' && (
                            <button
                              title="View"
                              style={actionBtnView}
                              onClick={() => handleView(content)}
                            >
                              <FaExternalLinkAlt size={12} /> View
                            </button>
                          )}
                          {content.status === "Submitted" && (isSystemAdmin || isOrgAdmin || isOwnedSite(content.siteId)) && (
                            <>
                              <button title="Approve" style={actionBtnApprove} onClick={() => setConfirmation({ id: content.id, type: 'APPROVE' })}><FaCheck /></button>
                              <button title="Reject" style={actionBtnReject} onClick={() => setConfirmation({ id: content.id, type: 'REJECT' })}><FaTimes /></button>
                            </>
                          )}
                          {content.status === "Approved" && (isSystemAdmin || isOrgAdmin) && (
                            <button title="Disable" style={actionBtnDisable} onClick={() => setConfirmation({ id: content.id, type: 'DISABLE' })}><FaBan size={12} /></button>
                          )}
                          {(isSystemAdmin || isOrgAdmin) && (
                            <button title="Delete" style={actionBtnDelete} onClick={() => setConfirmation({ id: content.id, type: 'DELETE' })}><FaTrash /></button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div style={paginationRow}>
              <div style={paginationInfoStyle}>Showing {currentItems.length} of {filteredContents.length}</div>
              <div style={paginationControlsWrapper}>
                <button style={pageArrow} disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}><FaChevronLeft /></button>
                {visiblePages.map(page => (
                  <button key={page} onClick={() => setCurrentPage(page)} style={{ ...pageNumberBtn, ...(currentPage === page ? activePage : {}) }}>{page}</button>
                ))}
                <button style={pageArrow} disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}><FaChevronRight /></button>
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .spin { animation: fa-spin 2s infinite linear; } 
        @keyframes fa-spin { from { transform: rotate(0deg); } to { transform: rotate(359deg); } }
        .kpi-card { transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); position: relative; overflow: hidden; }
        .kpi-card:hover { transform: translateY(-8px) scale(1.02); box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important; }
        .kpi-card:active { transform: translateY(-2px) scale(0.98); }
        .kpi-card::after { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: linear-gradient(225deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%); pointer-events: none; }
      `}</style>
    </div>
  );
}

// ============ DROPDOWN STYLES ============
const dropdownContainerStyle = { position: 'relative' };
const dropdownTriggerStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontWeight: 500, color: '#1e293b', transition: 'all 0.2s ease' };
const dropdownTriggerTextStyle = { flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const dropdownMenuStyle = { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, marginTop: 4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', zIndex: 1000, overflow: 'hidden', animation: 'fadeIn 0.2s ease-out' };
const searchBoxContainerStyle = { position: 'relative', padding: '8px 12px', borderBottom: '1px solid #f1f5f9', background: '#fcfdfe' };
const searchIconStyle = { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12, pointerEvents: 'none' };
const dropdownSearchInputStyle = { width: '100%', padding: '8px 12px 8px 28px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff' };
const dropdownListStyle = { maxHeight: '240px', overflowY: 'auto', padding: '4px' };
const dropdownOptionStyle = { width: '100%', padding: '10px 12px', border: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 6, margin: '2px 0', transition: 'all 0.2s ease', fontSize: 13 };
const optionTextStyle = { flex: 1 };
const optionNameStyle = { fontWeight: 600, color: 'inherit' };
const optionIdStyle = { fontSize: '11px', opacity: 0.6, marginTop: 2 };
const emptyOptionStyle = { padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: 13 };

// ============ MAIN STYLES ============
const kpiRowStyle = { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '20px', marginBottom: '32px', alignItems: 'stretch' };
const kpiCardBase = { padding: '24px', borderRadius: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minHeight: '160px', cursor: 'pointer', transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)', border: '1px solid transparent' };
const kpiCardWhite = { ...kpiCardBase, background: '#fff', color: '#1e293b', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' };
const kpiCardActiveAll = { ...kpiCardBase, border: '2px solid #6366f1', background: '#f5f3ff', color: '#1e293b', boxShadow: '0 10px 15px -3px rgba(99, 102, 241, 0.1)' };
const kpiCardActivePending = { ...kpiCardBase, border: '2px solid #f59e0b', background: '#fffbeb', color: '#1e293b', boxShadow: '0 10px 15px -3px rgba(245, 158, 11, 0.1)' };
const kpiCardActiveSubmitted = { ...kpiCardBase, border: '2px solid #a855f7', background: '#f3e8ff', color: '#1e293b', boxShadow: '0 10px 15px -3px rgba(168, 85, 247, 0.1)' };
const kpiCardActiveApproved = { ...kpiCardBase, border: '2px solid #10b981', background: '#f0fdf4', color: '#1e293b', boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.1)' };
const kpiCardActiveRejected = { ...kpiCardBase, border: '2px solid #ef4444', background: '#fef2f2', color: '#1e293b', boxShadow: '0 10px 15px -3px rgba(239, 68, 68, 0.1)' };
// Updated: Modern disabled state styling to match Flow component
const kpiCardActiveDisabled = {
  ...kpiCardBase,
  border: '2px solid #9ca3af',
  background: '#f9fafb',
  color: '#1e293b',
  boxShadow: '0 10px 15px -3px rgba(107, 114, 128, 0.1)'
};
const kpiHeader = { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' };
const kpiIconWrapper = { width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' };
const kpiLabelStyle = { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.05em' };
const kpiValueStyle = { fontSize: '32px', fontWeight: 800, marginBottom: '4px', color: '#1e293b' };
const kpiMetaStyle = { fontSize: '11px', opacity: 0.7, fontWeight: 500 };

const inlineConfirmWrapper = { display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '4px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', animation: 'fadeIn 0.2s ease-out' };
const confirmText = { fontSize: '11px', fontWeight: '700', color: '#475569' };
const btnYes = { background: '#6366f1', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', cursor: 'pointer' };
const btnNo = { background: '#fff', color: '#64748b', border: '1px solid #e2e8f0', padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', cursor: 'pointer' };
const wrapperStyle = { width: '100%', minHeight: '100vh', background: '#f8fafc', paddingBottom: 60, fontFamily: 'Inter, sans-serif' };
const containerStyle = { maxWidth: 1200, margin: '0 auto', padding: '24px' };
const headerStyle = { marginBottom: 32 };
const headerContentStyle = { display: 'flex', alignItems: 'center', gap: 16 };
const headerBadgeStyle = { width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' };
const titleStyle = { fontSize: 24, fontWeight: 800, margin: 0, color: '#0f172a' };
const filtersCardStyle = { background: '#fff', borderRadius: 16, padding: 24, marginBottom: 24, border: '1px solid #e2e8f0' };
const cardHeaderStyle = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, borderBottom: '1px solid #f1f5f9', paddingBottom: 12 };
const cardTitleStyle = { fontSize: 15, fontWeight: 700 };
const filtersGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, alignItems: 'end' };
const inputGroupStyle = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelStyle = { fontSize: 11, fontWeight: 700, color: '#64748b' };
const primaryButtonStyle = { background: '#6366f1', color: '#fff', border: 'none', padding: '11px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 };
const tableCardStyle = { background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' };
const toolbarStyle = { padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const searchWrapper = { position: 'relative', width: '300px' };
const searchIcon = { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' };
const searchInput = { width: '100%', padding: '9px 12px 9px 38px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, backgroundColor: '#fff', outline: 'none', color: '#3a4557' };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const tableHeaderRowStyle = { background: '#f8fafc' };
const thStyle = { padding: '14px 20px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'left' };
const thActionStyle = { ...thStyle, textAlign: 'right' };
const trStyle = { borderBottom: '1px solid #f1f5f9' };
const tdStyle = { padding: '16px 20px', fontSize: 14 };
const tdActionStyle = { padding: '16px 20px', textAlign: 'right' };
const contentNameText = { fontWeight: 700, color: '#1e293b' };
const contentIdText = { fontSize: '10px', color: '#94a3b8', marginTop: 2 };
const contentDescText = { fontSize: 11, color: '#64748b' };
const typeBadgeStyle = { background: '#f1f5f9', color: '#475569', padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700 };
const actionButtonGroupStyle = { display: 'inline-flex', gap: 8 };
const actionBtnBase = { width: 34, height: 34, borderRadius: 9, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const actionBtnView = { ...actionBtnBase, width: 'auto', padding: '0 12px', background: '#ede9fe', color: '#6366f1', fontSize: 11, fontWeight: 700, gap: 4 };
const actionBtnApprove = { ...actionBtnBase, background: '#dcfce7', color: '#166534' };
const actionBtnReject = { ...actionBtnBase, background: '#fee2e2', color: '#991b1b' };
const actionBtnDisable = { ...actionBtnBase, background: '#f3e8ff', color: '#7c3aed' };
const actionBtnDelete = { ...actionBtnBase, background: '#f1f5f9', color: '#64748b' };
const emptyStateStyle = { padding: '60px 20px', textAlign: 'center', color: '#94a3b8' };
const paginationRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', background: '#fff', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 16 };
const paginationInfoStyle = { fontSize: 14, color: '#64748b', fontWeight: 500 };
const paginationControlsWrapper = { display: 'flex', alignItems: 'center', gap: 8 };
const pageNumberBtn = { minWidth: 36, height: 36, padding: '0 4px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: '700', cursor: 'pointer', color: '#64748b' };
const activePage = { background: '#6366f1', color: '#fff', borderColor: '#6366f1', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)' };
const pageArrow = { ...pageNumberBtn, display: 'flex', alignItems: 'center', justifyContent: 'center' };